import { createWithEqualityFn } from 'zustand/traditional';
import {
  applyNodeChanges,
  applyEdgeChanges,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  getConnectedEdges,
  Viewport,
  NodePositionChange,
  ReactFlowInstance, // Import ReactFlowInstance
} from "reactflow";
import { v4 as uuidv4 } from 'uuid';
import { CustomNode, Project, AnyNodeData, DestinationNodeData, HandleConfig } from "../types";
import { loadState, useStatePersistence, saveProjectId } from './persistence';
import { reportService } from '../services/report.service';
import { getHelperLines } from '../utils/helperLines';
import { debounce } from '../components/Canvas/utils'; // Import debounce
import { getDefaultNodeWidth } from '../utils/nodeHelpers'; // Import NODE_DIMENSIONS and getDefaultNodeWidth

// Define the structure for a state snapshot
interface FlowSnapshot {
  nodes: CustomNode[];
  edges: Edge[];
}

import { HelperLineSegment } from '../utils/helperLines';

export interface FlowState {
  currentProject: Project | null;
  setProject: (project: Project | null) => void;
  setNodes: (nodes: Node[] | ((prevNodes: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  addNode: (node: CustomNode) => void;
  updateNode: <T extends AnyNodeData>(nodeId: string, data: Partial<T>) => void;
  deleteNode: (nodeId: string) => void;
  deleteEdge: (edgeId: string) => void;
  selectedEdge: string | null;
  setSelectedEdge: (edgeId: string | null) => void;
  selectedNode: CustomNode | null;
  setSelectedNode: (node: CustomNode | null) => void;
  nodes: CustomNode[];
  edges: Edge[];
  clusterLatencies: Record<string, number>;
  recalculateClusterLatencies: () => void;
  viewport?: Viewport;
  setViewport: (viewport: Viewport) => void;
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  isNodeDragging: boolean;
  setIsNodeDragging: (isDragging: boolean) => void;
  helperLineHorizontal?: number;
  helperLineVertical?: number;
  fixedLines: HelperLineSegment[];
  spacingLines: HelperLineSegment[];
  setHelperLines: (horizontal?: number, vertical?: number, fixed?: HelperLineSegment[], spacing?: HelperLineSegment[]) => void;
  hoveredGroupId: string | null; // Track group being hovered over during drag
  setHoveredGroupId: (groupId: string | null) => void;
  loadLayoutFromCsv: (nodes: Node[], edges: Edge[]) => void;
  // Debounced latency calculation functions (internal)
  _performLatencyRecalculation: () => void; // Add the missing declaration
  _debouncedRecalculateLatencies: () => void;

  // Undo/Redo State
  past: FlowSnapshot[];
  future: FlowSnapshot[];

  // Undo/Redo Actions
  takeSnapshot: () => void;
  undo: () => boolean; // Return true if successful, false otherwise
  redo: () => boolean; // Return true if successful, false otherwise

  // Undo/Redo Selectors (derived state)
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Store React Flow instance for internal use (like getting nodes/edges in takeSnapshot)
  reactFlowInstance: ReactFlowInstance | null;
  setReactFlowInstance: (instance: ReactFlowInstance | null) => void;

  // Panel Visibility State
  isLeftPanelManuallyCollapsed: boolean;
  isRightPanelManuallyCollapsed: boolean;
  togglePanels: () => void;

  // Flag for batch updates to prevent nested snapshots
  isBatchUpdating: boolean;
  addNodesAtomically: (nodesToAdd: Node[]) => void; // For adding multiple nodes at once
}

// Process nodes: Convert reactflow Node[] to internal CustomNode[], handle null width/height, restore handlers
const processNodes = (inputNodes: Node[] | Node, get: () => FlowState): CustomNode[] => {
  // Ensure inputNodes is always an array
  const nodeArray = Array.isArray(inputNodes) ? inputNodes : [inputNodes];

  return nodeArray.map((node) => {
    // Grid snapping is handled elsewhere (e.g., Canvas onNodesChange wrapper)
    // Use the node's position directly as potentially modified by alignment snapping
    const currentPosition = { x: node.position.x, y: node.position.y };

    // Set default width to DEFAULT_WIDTH from NODE_DIMENSIONS if width is not specified
    // This ensures newly created nodes have a reasonable width
    const processedWidth = node.width ?? getDefaultNodeWidth(node.type || '');
    const processedHeight = node.height ?? undefined;

    // Use the actual node dimensions instead of calculating them
    // This fixes the issue with the selection area being much larger than the actual node
    // Ensure group nodes have the required properties
    let nodeData = node.data as AnyNodeData; // Use AnyNodeData type

    // If this is a group node, ensure it has all required properties
    if (node.type === 'group') {
      const groupData = node.data as any; // Keep as any for flexibility here
      nodeData = {
        ...groupData,
        hasIncomingHandle: groupData.hasIncomingHandle ?? false,
        hasOutgoingHandle: groupData.hasOutgoingHandle ?? false,
        color: groupData.color, // Don't set a default color, keep it undefined
        icon: groupData.icon ?? 'box',
        groupNameVisibility: groupData.groupNameVisibility ?? 'visible',
        iconVisibility: groupData.iconVisibility ?? 'visible',
        customLabelVisibility: groupData.customLabelVisibility ?? 'visible',
        allowedNodeTypes: groupData.allowedNodeTypes ?? [],
      };
    } else if (node.type === 'destination') {
      // Explicitly handle destination node data, ensuring latencyImpact is present and potentially typed
      const destData = node.data as DestinationNodeData; // Cast to specific type
      // MERGE instead of overwrite to preserve all original data like 'handles'
      nodeData = {
        ...node.data, // Keep all original data from the input node
        type: 'destination', // Ensure type is correctly set/overridden if needed
        // Ensure latencyImpact is present and correctly typed
        latencyImpact: destData.latencyImpact ?? 0, // Default to 0 if missing
        // Add/override other destination-specific fields if necessary
      };
    }
    // Add similar 'else if' blocks for other specific node types if necessary, ensuring they also preserve original data

    // Create the processed node, ensuring it matches CustomNode structure
    const processedNode: CustomNode = { // Explicitly type as CustomNode
      ...node,
      width: processedWidth, // Use processed width
      height: processedHeight, // Use processed height
      position: currentPosition, // Use the position potentially snapped by getHelperLines
      data: {
        ...nodeData,
        // Restore handlers if they exist on original data, otherwise define defaults
        onDelete: node.data?.onDelete ?? ((nodeId: string) => { get().deleteNode(nodeId); }),
        onUpdate: node.data?.onUpdate ?? ((updatedData: Partial<AnyNodeData>) => { get().updateNode(node.id, updatedData); }),
      },
      // Ensure other CustomNode specific properties are handled if necessary
      selected: node.selected ?? false,
      dragging: node.dragging ?? false,
      // Use currentPosition for positionAbsolute default as well
      positionAbsolute: node.positionAbsolute ?? currentPosition,
    };

    // If the node has a parentId, ensure it's properly preserved
    // Note: React Flow's Node type includes parentId, extent, expandParent. CustomNode might too.
    // If CustomNode doesn't have these, they should be omitted. Assuming they are part of CustomNode for now.
    if (node.parentId) {
      processedNode.parentId = node.parentId;
      processedNode.extent = node.extent; // Preserve extent if present
      processedNode.expandParent = node.expandParent ?? false; // Default expandParent
    } else {
      // Ensure these properties are undefined if no parentId exists
      delete processedNode.parentId;
      delete processedNode.extent;
      delete processedNode.expandParent;
    }

    return processedNode;
  });
};


// Load persisted state or use defaults
const persistedState = loadState();

// Load panel visibility from localStorage
const initialLeftPanelCollapsed = localStorage.getItem('leftPanelManuallyCollapsed') === 'true';
const initialRightPanelCollapsed = localStorage.getItem('rightPanelManuallyCollapsed') === 'true';


// Initialize with persisted state or defaults
const initialEdges: Edge[] = persistedState?.edges ?? [];
const initialViewport: Viewport | undefined = persistedState?.viewport;
// Load nodes as Node[] initially, they will be processed later
const initialNodesToProcess: Node[] = (persistedState?.nodes as Node[]) ?? [];

// Subscribe to state changes for persistence
const subscribeToChanges = (state: FlowState) => {
  // Persist only the necessary parts, nodes might need to be stored as Node[] if CustomNode has functions
  // Exclude undo/redo history from persistence
  const { past, future, reactFlowInstance, ...stateToPersist } = state;
  useStatePersistence({ ...stateToPersist });
};

// Helper function to validate edges against available handles
const validateEdgesWithHandles = (nodes: CustomNode[], edges: Edge[]): Edge[] => {
  // Create a map of all available handles on all nodes
  const handleMap = new Map<string, Set<string>>();
  
  // Populate the handle map with all available handles
  nodes.forEach(node => {
    // Skip nodes without data
    if (!node.data) return;
    
    // Create sets for source and target handles if they don't exist
    if (!handleMap.has(node.id)) {
      handleMap.set(node.id, new Set<string>());
    }
    
    // Check if this node type has explicit handles
    const nodeData = node.data as any; // Use any temporarily to check for handles
    
    if (nodeData.handles && Array.isArray(nodeData.handles)) {
      // Process explicit handles
      const nodeHandles = nodeData.handles as HandleConfig[];
      
      // Add each handle ID to the set for this node
      nodeHandles.forEach(handle => {
        handleMap.get(node.id)?.add(handle.id);
      });
    }
    
    // For group nodes, always add default source and target handles
    // This ensures edges to/from groups are preserved during undo/redo
    if (node.type === 'group') {
      // Add default group handles in the format `${id}-source` and `${id}-target`
      handleMap.get(node.id)?.add(`${node.id}-source`);
      handleMap.get(node.id)?.add(`${node.id}-target`);
    }
  });
  
  // Filter edges to only include those with valid handles
  return edges.filter(edge => {
    // Skip validation for edges without handle IDs (default handles)
    if (!edge.sourceHandle && !edge.targetHandle) return true;
    
    // For edges with source/target handles, check if the nodes exist
    const sourceNodeExists = nodes.some(n => n.id === edge.source);
    const targetNodeExists = nodes.some(n => n.id === edge.target);
    
    if (!sourceNodeExists || !targetNodeExists) {
      return false; // Skip edges where nodes don't exist
    }
    
    // Check if source handle exists (if specified)
    if (edge.sourceHandle) {
      const sourceHandles = handleMap.get(edge.source);
      // If the node has no handles map entry but exists, assume it's using default handles
      if (sourceHandles && !sourceHandles.has(edge.sourceHandle)) {
        return false;
      }
    }
    
    // Check if target handle exists (if specified)
    if (edge.targetHandle) {
      const targetHandles = handleMap.get(edge.target);
      // If the node has no handles map entry but exists, assume it's using default handles
      if (targetHandles && !targetHandles.has(edge.targetHandle)) {
        return false;
      }
    }
    
    // Edge has valid handles or is using default handles
    return true;
  });
};

// Create a more robust flag system for undo/redo operations
// This ensures we don't take snapshots during undo/redo operations
const undoRedoState = {
  isRestoring: false,
  setIsRestoring: (value: boolean) => {
    undoRedoState.isRestoring = value;
  }
};

// Keep the original type definition to maintain compatibility
const useStore = createWithEqualityFn<FlowState & { isRestoring: boolean }>((set, get) => ({
  currentProject: null,
  nodes: [], // Start with empty, will be populated by processing initialNodesToProcess
  edges: initialEdges,
  selectedEdge: null,
  selectedNode: null,
  clusterLatencies: {},
  viewport: initialViewport,
  isInitialized: false, // Initialize after processing initial nodes
  isRestoring: false, // Flag to disable snapshotting during undo/redo
  isBatchUpdating: false, // Initialize batch updating flag
  setIsInitialized: (isInitialized: boolean) => set({ isInitialized }),
  isNodeDragging: false,
  setIsNodeDragging: (isDragging: boolean) => {
    set({ isNodeDragging: isDragging });
  },
  helperLineHorizontal: undefined,
  helperLineVertical: undefined,
  fixedLines: [],
  spacingLines: [],
  setHelperLines: (horizontal?: number, vertical?: number, fixed?: HelperLineSegment[], spacing?: HelperLineSegment[]) => set({
    helperLineHorizontal: horizontal,
    helperLineVertical: vertical,
    fixedLines: fixed || [],
    spacingLines: spacing || []
  }),
  hoveredGroupId: null, // Initialize hovered group ID
  setHoveredGroupId: (groupId: string | null) => set({ hoveredGroupId: groupId }),

  // Panel Visibility State & Actions
  isLeftPanelManuallyCollapsed: initialLeftPanelCollapsed,
  isRightPanelManuallyCollapsed: initialRightPanelCollapsed,
  togglePanels: () => {
    set((state) => {
      const newLeftCollapsed = !state.isLeftPanelManuallyCollapsed;
      const newRightCollapsed = !state.isRightPanelManuallyCollapsed;
      localStorage.setItem('leftPanelManuallyCollapsed', String(newLeftCollapsed));
      localStorage.setItem('rightPanelManuallyCollapsed', String(newRightCollapsed));
      return {
        isLeftPanelManuallyCollapsed: newLeftCollapsed,
        isRightPanelManuallyCollapsed: newRightCollapsed,
      };
    });
  },

  // Undo/Redo State
  past: [],
  future: [],
  reactFlowInstance: null, // Initialize reactFlowInstance

  setReactFlowInstance: (instance: ReactFlowInstance | null) => set({ reactFlowInstance: instance }),

  // --- Debounced Latency Calculation ---
  // Define the core logic separately
  _performLatencyRecalculation: () => {
    const newClusterLatencies: Record<string, number> = {};
    const { nodes, edges } = get(); // nodes here are CustomNode[]

    const isDestinationNodeData = (data: AnyNodeData): data is DestinationNodeData => {
      return data.type === 'destination';
    };

    nodes.filter((node) => node.type === 'cluster')
      .forEach((clusterNode) => {
        const connectedEdges = getConnectedEdges([clusterNode], edges);

        const connectedDestinationNodesData = connectedEdges.reduce(
          (acc: DestinationNodeData[], edge: Edge) => {
            const targetNode = nodes.find((node) => node.id === edge.target);
            if (targetNode && targetNode.data && isDestinationNodeData(targetNode.data)) {
              return [...acc, targetNode.data];
            }
            return acc;
          },
          [],
        );

        const totalLatencyImpact = connectedDestinationNodesData.reduce(
          (sum: number, data: DestinationNodeData) => {
            const impact = data.latencyImpact;
            const numericImpact = typeof impact === 'string' ? parseInt(impact, 10) : Number(impact);
            return sum + (isNaN(numericImpact) ? 0 : numericImpact);
          },
          0,
        );

        newClusterLatencies[clusterNode.id] = totalLatencyImpact;
      });

    set({ clusterLatencies: newClusterLatencies });
  },

  // Create the debounced version
  _debouncedRecalculateLatencies: debounce(() => {
    get()._performLatencyRecalculation();
  }, 250), // Debounce delay of 250ms

  // Public function to trigger the debounced calculation
  recalculateClusterLatencies: () => {
    get()._debouncedRecalculateLatencies();
  },
  // --- End Debounced Latency Calculation ---


  setSelectedNode: (node: CustomNode | null) => {
    set({ selectedNode: node });
  },

  setSelectedEdge: (edgeId: string | null) => {
    set({ selectedEdge: edgeId });
  },

  // recalculateClusterLatencies is now the trigger for the debounced function (defined above)

  setProject: (project: Project | null) => {
    try {
      if (project) {
        // Process nodes from the project (which are likely FlowNode[]) into CustomNode[]
        const processedNodes = processNodes(project.nodes, get); // processNodes now accepts Node[]

        const processedEdges = project.edges.map((edge: Edge) => {
          // Ensure sourceHandle is never null or undefined, but an empty string
          // IMPORTANT: Group nodes require specific handle IDs in the format `${id}-source` and `${id}-target`
          // This ensures that edges connect to the correct handles and prevents the
          // "Couldn't create edge for source handle id: ''" error
          // For group nodes, ensure the handle ID follows the correct format
          let sourceHandle = edge.sourceHandle;
          const sourceNode = processedNodes.find(n => n.id === edge.source); // Find in processed CustomNode[]
          const targetNode = processedNodes.find(n => n.id === edge.target); // Find in processed CustomNode[]

          if (sourceNode?.type === 'group' && (!sourceHandle || sourceHandle === '')) {
            sourceHandle = `${sourceNode.id}-source`; // Use explicit group handle format
          } else if (sourceHandle == null) { // Check for null or undefined
            sourceHandle = ''; // Default for non-group or if explicitly null/undefined
          }

          let targetHandle = edge.targetHandle;
          if (targetNode?.type === 'group' && (!targetHandle || targetHandle === '')) {
            targetHandle = `${targetNode.id}-target`; // Use explicit group handle format
          }

          // Refined logic to prioritize loaded properties
          const loadedStyle = edge.style || {};
          const loadedData = edge.data || {};
          const finalStroke = loadedStyle.stroke || loadedData.color || '#6b7280';
          const finalStrokeWidth = loadedStyle.strokeWidth || loadedData.thickness || 2;

          // Define processedEdge before return
          const processedEdge = {
            ...edge, // Spread the loaded edge first
            id: edge.id ?? uuidv4(),
            source: edge.source,
            target: edge.target,
            sourceHandle: sourceHandle,
            targetHandle: targetHandle,
            animated: edge.animated ?? false,
            style: { // Construct style object carefully
                ...loadedStyle, // Spread original style first
                stroke: finalStroke,
                strokeWidth: finalStrokeWidth,
            },
            markerStart: edge.markerStart, // Directly use loaded marker
            markerEnd: edge.markerEnd,     // Directly use loaded marker
            data: { // Keep data primarily for config state + label/shape/points
                ...(loadedData), // Spread original data
                shape: loadedData.shape || 'default',
                label: loadedData.label || '',
                points: loadedData.points || [],
                // Ensure data reflects the final color/thickness for UI consistency
                color: finalStroke,
                thickness: finalStrokeWidth,
            },
            type: edge.type || 'editable-edge', // Preserve type
          };

          // --- Consistent Label Handling ---
          const edgeData = processedEdge.data || {};
          const edgeLabel = edgeData.label || processedEdge.label || ''; // Get label from data or top-level

          // Always store label in data, clear top-level label
          edgeData.label = edgeLabel;
          delete processedEdge.label;
          processedEdge.data = edgeData; // Assign potentially modified data back
          // --- End Consistent Label Handling ---
          
          return processedEdge; // Return the modified edge
        });

        set({
          currentProject: project,
          nodes: processedNodes, // Set the processed CustomNode[]
          edges: processedEdges,
          isInitialized: true,
          past: [], // Clear history on new project load
          future: [],
        });

        // --- ADDED: Fit view after setting project ---
        setTimeout(() => {
          get().reactFlowInstance?.fitView({ padding: 0.2, duration: 200 });
        }, 0);
        // --- END ADDED ---

      } else {
        // Clear report settings from localStorage when closing a project
        reportService.clearReportFromLocalStorage();

        set({ currentProject: null, nodes: [], edges: [], isInitialized: false, past: [], future: [] });
      }
    } catch (error) {
      console.error('Error in setProject:', error);
    }
  },

  setNodes: (nodes: Node[] | ((prevNodes: Node[]) => Node[])) => {
    const { isBatchUpdating, nodes: currentNodes } = get();
    let newNodesArray: CustomNode[];

    if (typeof nodes === 'function') {
      const newCustomNodes = nodes(currentNodes) as CustomNode[]; // Assume function returns CustomNode[]
      if (!isBatchUpdating) {
        if (newCustomNodes.length !== currentNodes.length || 
            newCustomNodes.some(n => !currentNodes.find(cn => cn.id === n.id)) ||
            currentNodes.some(cn => !newCustomNodes.find(n => n.id === cn.id))) {
          get().takeSnapshot();
        }
      }
      newNodesArray = processNodes(newCustomNodes, get);
    } else {
      if (!isBatchUpdating) {
        if (nodes.length !== currentNodes.length ||
            nodes.some(n => !currentNodes.find(cn => cn.id === n.id)) ||
            currentNodes.some(cn => !nodes.find(n => n.id === cn.id))) {
          get().takeSnapshot();
        }
      }
      newNodesArray = processNodes(nodes, get);
    }
    set({ nodes: newNodesArray });
  },

  setEdges: (edges: Edge[]) => {
    const { isBatchUpdating, edges: currentEdges } = get();
    if (!isBatchUpdating) {
      if (edges.length !== currentEdges.length ||
          edges.some(e => !currentEdges.find(ce => ce.id === e.id)) ||
          currentEdges.some(ce => !edges.find(e => e.id === ce.id))) {
        get().takeSnapshot();
      }
    }
    set({ edges });
  },

  onNodesChange: (changes: NodeChange[]) => {
    const { isBatchUpdating } = get();
    const nonSelectionChanges = changes.filter(c => c.type !== 'select');
    
    // If all changes were selection changes, just apply them without taking a snapshot
    if (nonSelectionChanges.length === 0) {
      const currentNodes = get().nodes;
      const updatedNodes = applyNodeChanges(changes, currentNodes);
      const finalNodes = processNodes(updatedNodes, get);
      set({ nodes: finalNodes });
      return;
    }
    
    // For non-selection changes, proceed with normal processing
    const isDraggingChange = nonSelectionChanges.some((c) => c.type === 'position' && c.dragging);
    const isAddChange = nonSelectionChanges.some((c) => c.type === 'add');
    
    if (!isBatchUpdating && isAddChange && !isDraggingChange) {
      get().takeSnapshot();
    }

    // Reset helper lines when nodes change
    set({ helperLineHorizontal: undefined, helperLineVertical: undefined });

    const currentNodes = get().nodes; // These are CustomNode[]

    // Process changes, applying them to CustomNode[]
    // applyNodeChanges expects NodeChange[] and Node[] (reactflow types)
    // but operates on the structure, so it should work with CustomNode[] if compatible
    let updatedNodes = applyNodeChanges(changes, currentNodes); // Result should be CustomNode[]

    // --- Existing logic for helper lines, group dragging, etc. ---
    // This logic likely assumes the structure of CustomNode
    const processedChanges = changes.map(change => {
       if (change.type === 'position') {
         const node = currentNodes.find(n => n.id === change.id);
         if (!node) return change;

         // Restore parentId logic (assuming CustomNode has parentId)
         const nodeData = node.data as any;
         if (nodeData?.__inGroup && nodeData?.__groupId && !node.parentId) {
           const groupId = nodeData.__groupId;
           const groupNode = currentNodes.find(n => n.id === groupId);
           if (groupNode) node.parentId = groupId;
         }

         // Preserve position on click logic (assuming CustomNode has parentId)
         if (!change.dragging && node.parentId && change.position) {
           return { ...change, position: { ...node.position } };
         }

         // Dragging logic (assuming CustomNode has width/height/parentId)
         if (change.dragging && change.position) {
           // Get viewport bounds for helper lines
           let viewBounds;
           
           // Use a simpler approach with fixed large bounds
           // This will make helper lines extend across the entire visible area
           viewBounds = {
             x: -5000,
             y: -5000,
             x2: 5000,
             y2: 5000
           };
           
           // Get helper lines with viewport bounds
           const helperLines = getHelperLines(change, currentNodes, node?.parentId ?? null, viewBounds);
           
           // Apply snap position if available
           if (helperLines.snapPosition.x !== undefined || helperLines.snapPosition.y !== undefined) {
             change.position.x = helperLines.snapPosition.x ?? change.position.x;
             change.position.y = helperLines.snapPosition.y ?? change.position.y;
             
             // Extract different types of lines from the result
             const horizontalLine = helperLines.lines.find(line => line.y1 === line.y2 && line.style === 'align');
             const verticalLine = helperLines.lines.find(line => line.x1 === line.x2 && line.style === 'align');
             const fixedLines = helperLines.lines.filter(line => line.style === 'fixed');
             const spacingLines = helperLines.lines.filter(line => line.style === 'spacing');
             
             set({ 
               helperLineHorizontal: horizontalLine ? horizontalLine.y1 : undefined, 
               helperLineVertical: verticalLine ? verticalLine.x1 : undefined,
               fixedLines: fixedLines,
               spacingLines: spacingLines
             });
           } else {
             // Clear helper lines if no snap position
             set({ 
               helperLineHorizontal: undefined, 
               helperLineVertical: undefined,
               fixedLines: [],
               spacingLines: []
             });
           }

           // Dragging out of group logic
           if (node.parentId) {
             const parentGroup = currentNodes.find(n => n.id === node.parentId);
             if (parentGroup) {
               const isInsideGroup = change.position.x >= 0 && change.position.x <= (parentGroup.width || 0) &&
                                     change.position.y >= 0 && change.position.y <= (parentGroup.height || 0);
               if (!isInsideGroup && !change.dragging) {
                 const absolutePosition = { x: change.position.x + parentGroup.position.x, y: change.position.y + parentGroup.position.y };
                 return { ...change, position: absolutePosition, __removeFromParent: true };
               }
             }
           }
         }
       }
       return change;
     });

     // Apply potentially modified changes again (React Flow example pattern)
     // The result might be Node[] type from reactflow
     let intermediateNodes = applyNodeChanges(processedChanges, currentNodes);

     // Handle nodes marked for removal from parent
     const nodesToRemoveFromParent = processedChanges
       .filter(change => change.type === 'position' && (change as any).__removeFromParent)
       .map(change => (change as NodePositionChange).id);

     if (nodesToRemoveFromParent.length > 0) {
       updatedNodes = updatedNodes.map(node => {
         if (nodesToRemoveFromParent.includes(node.id)) {
           const { parentId, extent, expandParent, ...rest } = node; // Remove parent-related props
           return { ...rest, draggable: true, selectable: true, zIndex: 1000 };
         }
         return node;
       });
     }

     // --- End of existing logic ---

    // CRITICAL: Re-process the nodes after applying changes to ensure they match CustomNode[] type
    const finalNodes = processNodes(intermediateNodes, get);
    set({ nodes: finalNodes }); // Set the correctly typed CustomNode[]

  },


  onEdgesChange: (changes: EdgeChange[]) => {
    const { isBatchUpdating } = get();
    // Filter out selection changes completely - we don't want them in the undo history
    const nonSelectionChanges = changes.filter(c => c.type !== 'select');

    // If all changes were selection changes, just apply them without taking a snapshot
    if (nonSelectionChanges.length === 0) {
      const newEdges = applyEdgeChanges(changes, get().edges);
      set({ edges: newEdges });
      return;
    }

    // For non-selection changes, proceed with normal processing
    const isAddChange = nonSelectionChanges.some((c) => c.type === 'add');
    if (!isBatchUpdating && isAddChange) {
      get().takeSnapshot();
    }

    const newEdges = applyEdgeChanges(changes, get().edges);
    set({ edges: newEdges });
    get().recalculateClusterLatencies();
  },

  // onConnect is handled in useConnectionHandlers.ts, not directly in useStore.ts
  // The edge creation logic needs to be updated there.

  addNode: (node: CustomNode) => {
    set({ isBatchUpdating: true });
    try {
      get().takeSnapshot(); // Snapshot before adding
      // Process the single node before adding
      const processedNode = processNodes(node, get)[0];
      set({
        nodes: [...get().nodes, processedNode],
      });
    } finally {
      setTimeout(() => {
        set({ isBatchUpdating: false });
      }, 0);
    }
  },

  addNodesAtomically: (nodesToAdd: Node[]) => {
    set({ isBatchUpdating: true });
    try {
      get().takeSnapshot(); // Take one snapshot before adding all nodes
      const processedNodesToAdd = processNodes(nodesToAdd, get); // processNodes handles conversion to CustomNode[]
      set((state) => ({
        nodes: [...state.nodes, ...processedNodesToAdd], // Add all processed nodes
      }));
    } finally {
      // Reset isBatchUpdating immediately after the operation
      set({ isBatchUpdating: false });
    }
  },

  updateNode: <T extends AnyNodeData>(nodeId: string, data: Partial<T>) => {
    set({ isBatchUpdating: true });
    try {
      const nodes = get().nodes;
      const nodeToUpdate = nodes.find(node => node.id === nodeId);
      if (!nodeToUpdate) {
        return;
      }

      get().takeSnapshot();

      // Merge data, ensuring not to overwrite essential functions if they exist
      const updatedData = {
        ...nodeToUpdate.data,
        ...data, // This now includes the potentially updated 'handles' array
        onDelete: nodeToUpdate.data.onDelete, // Preserve existing handlers
        onUpdate: nodeToUpdate.data.onUpdate,
      };

      // Create a *new* node object to ensure reference change
      // Explicitly type updatedNode as CustomNode to prevent overly specific type inference.
      let updatedNode: CustomNode = {
          ...nodeToUpdate, // Copy existing properties (id, type, position, etc.)
          data: updatedData, // Assign the new data object
      };

      // Process the updated node to ensure it conforms to CustomNode and has correct handlers/properties
      updatedNode = processNodes(updatedNode, get)[0];

      // Create a new nodes array with the updated node reference
      const newNodes = get().nodes.map(node =>
          node.id === nodeId ? updatedNode : node
      );

      set({ nodes: newNodes }); // Set the new array reference

      // Recalculate latencies if necessary
      // Check original type and potentially updated type
      if (nodeToUpdate.type === 'cluster' || (data.type === 'destination' && nodeToUpdate.type !== 'destination') || nodeToUpdate.type === 'destination') {
        get().recalculateClusterLatencies();
      }

      // No need to compare snapshots here to decide on taking a snapshot,
      // as we've already taken it unconditionally before the change.
      // The takeSnapshot function itself has logic to prevent duplicate identical snapshots.

      // Update selected node if it matches
    const selectedNode = get().selectedNode;
    if (selectedNode && selectedNode.id === nodeId) {
      // Ensure the selectedNode reference is updated to the potentially new object
      const finalUpdatedNode = get().nodes.find(node => node.id === nodeId);
      if (finalUpdatedNode) {
        set({ selectedNode: finalUpdatedNode });
      }
    }
    } finally {
      setTimeout(() => {
        set({ isBatchUpdating: false });
      }, 0);
    }
  },


  deleteNode: (nodeId: string) => {
    try {
      set({ isBatchUpdating: true }); // Start batch update
      get().takeSnapshot(); // Take snapshot *after* setting batch mode, but before changes.

      const allNodes = get().nodes; // CustomNode[]
      const nodeToDelete = allNodes.find((node) => node.id === nodeId);

    if (!nodeToDelete) {
      return;
    }

    const nodesToDeleteIds: string[] = [nodeId];

    // If it's a group, find all descendants recursively
    if (nodeToDelete.type === 'group') {
      const collectDescendantNodeIds = (groupId: string, nodes: CustomNode[]): string[] => {
        const descendants: string[] = [];
        const stack = [groupId];
        while (stack.length > 0) {
          const currentGroupId = stack.pop()!;
          const children = nodes.filter(n => n.parentId === currentGroupId);
          for (const child of children) {
            descendants.push(child.id);
            if (child.type === 'group') {
              stack.push(child.id);
            }
          }
        }
        return descendants;
      };

      const descendantIds = collectDescendantNodeIds(nodeId, allNodes);
      nodesToDeleteIds.push(...descendantIds);
    }

    // Find connected edges
    const edgesToRemove = get().edges.filter(
      (edge) => nodesToDeleteIds.includes(edge.source) || nodesToDeleteIds.includes(edge.target)
    );

    // Apply removals - applyNodeChanges returns Node[]
    const nodesAfterDeletion = applyNodeChanges(nodesToDeleteIds.map(id => ({ type: 'remove', id })), get().nodes);

    // Build a map of deleted nodes *before* deletion for position adjustment
    const deletedNodeMap = new Map<string, CustomNode>();
    allNodes.forEach(n => {
      if (nodesToDeleteIds.includes(n.id)) {
        deletedNodeMap.set(n.id, n);
      }
    });

    // Clear parentId for any remaining node whose parent was deleted, and convert position to absolute
    // Apply this cleanup *directly* to the nodesAfterDeletion array (which is Node[] type at this point)
    const cleanedNodesAfterDeletion = nodesAfterDeletion.map(node => {
      if (node.parentId && nodesToDeleteIds.includes(node.parentId)) {
        const deletedParent = deletedNodeMap.get(node.parentId);
        let newPosition = { ...node.position };
        if (deletedParent) {
          // Use the parent's position from the map (which is CustomNode)
          newPosition = {
            x: newPosition.x + deletedParent.position.x,
            y: newPosition.y + deletedParent.position.y,
          };
        }
        // Remove parent-related properties directly from the Node object
        const { parentId, extent, expandParent, ...rest } = node;
        return { ...rest, position: newPosition }; // Return a Node without parent info
      }
      return node; // Return unchanged Node
    });

    // Now, process the cleaned nodes into CustomNode[]
    const finalNodes = processNodes(cleanedNodesAfterDeletion, get);

      set({
        edges: applyEdgeChanges(edgesToRemove.map((edge) => ({ type: 'remove', id: edge.id })), get().edges),
        nodes: finalNodes, // Use the final, processed, and cleaned nodes
      });

      // Clear selection if deleted node was selected
      const selectedNode = get().selectedNode;
      if (selectedNode && nodesToDeleteIds.includes(selectedNode.id)) {
        set({ selectedNode: null });
      }

      // Recalculate latencies after deletion
      get().recalculateClusterLatencies();
    } finally {
      // Ensure isBatchUpdating is reset even if errors occur
      setTimeout(() => {
        set({ isBatchUpdating: false });
      }, 0);
    }
  },


  deleteEdge: (edgeId: string) => {
    set({ isBatchUpdating: true });
    try {
      get().takeSnapshot(); // Snapshot before deleting
      set((state) => ({ // Use functional update for safety
        edges: state.edges.filter((edge: Edge) => edge.id !== edgeId),
      }));
      get().recalculateClusterLatencies(); // Recalculate latencies after edge deletion
    } finally {
      setTimeout(() => {
        set({ isBatchUpdating: false });
      }, 0);
    }
  },

  setViewport: (viewport: Viewport) => {
    set({ viewport });
  },

  // Implementation for the new action
  loadLayoutFromCsv: (nodesToLoad: Node[], edgesToLoad: Edge[]) => {
    try {
      const processedNodes = processNodes(nodesToLoad, get); // Process input Node[] to CustomNode[]
      set({
        nodes: processedNodes,
        // Apply the same refined logic for CSV loading
        edges: edgesToLoad.map(e => {
          const loadedStyle = e.style || {};
          const loadedData = e.data || {};
          const finalStroke = loadedStyle.stroke || loadedData.color || '#6b7280';
          const finalStrokeWidth = loadedStyle.strokeWidth || loadedData.thickness || 2;

          // Define processedEdge before return
          const processedEdge = {
            ...e, // Spread the loaded edge first
            id: e.id ?? uuidv4(),
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle, // Assume handles are correct in CSV data
            targetHandle: e.targetHandle,
            animated: e.animated ?? false,
            style: {
                ...loadedStyle,
                stroke: finalStroke,
                strokeWidth: finalStrokeWidth,
            },
            markerStart: e.markerStart,
            markerEnd: e.markerEnd,
            data: {
                ...(loadedData),
                shape: loadedData.shape || 'default',
                label: loadedData.label || '',
                points: loadedData.points || [],
                color: finalStroke,
                thickness: finalStrokeWidth,
            },
            type: e.type || 'editable-edge',
          };

           // --- Consistent Label Handling (CSV Load) ---
           const edgeDataCsv = processedEdge.data || {};
           const edgeLabelCsv = edgeDataCsv.label || processedEdge.label || ''; // Get label

           // Always store label in data, clear top-level label
           edgeDataCsv.label = edgeLabelCsv;
           delete processedEdge.label;
           processedEdge.data = edgeDataCsv; // Assign potentially modified data back
           // --- End Consistent Label Handling ---

          // Log processed edge focusing on relevant properties
          return processedEdge; // Return the modified edge
        }),
        currentProject: null, // Clear current project context
        selectedNode: null,
        selectedEdge: null,
        isInitialized: true, // Mark as initialized
        past: [], // Clear history
        future: [],
      });
      // Clear persistence related to project ID
      saveProjectId(null);
      reportService.clearReportFromLocalStorage();
      // Optionally trigger recalculation if needed
      get().recalculateClusterLatencies();

      // --- ADDED: Fit view after loading CSV layout ---
      setTimeout(() => {
        get().reactFlowInstance?.fitView({ padding: 0.15, duration: 200 });
      }, 0);
      // --- END ADDED ---

    } catch (error) {
      console.error('[Store] Error loading layout from CSV:', error);
      // Optionally set an error state or use a notification system if available outside the store
    }
  },

  // --- Undo/Redo Implementation ---
  takeSnapshot: () => {
    
    if (undoRedoState.isRestoring) {
      return;
    }

    // Get current state
    const { past, nodes: currentNodesRaw, edges: currentEdgesRaw } = get(); // Added isBatchUpdating
    const currentNodesClone = JSON.parse(JSON.stringify(currentNodesRaw)) as CustomNode[];
    const currentEdgesClone = JSON.parse(JSON.stringify(currentEdgesRaw)) as Edge[];

    // Exclude selection/deselection from snapshots
    const currentNodes = currentNodesClone.map((node) => ({
      ...node,
      selected: false,
      dragging: false,
    }));
    
    // Check if this state is different from the last snapshot
    // This prevents duplicate snapshots for the same state
    const lastSnapshot = past.length > 0 ? past[past.length - 1] : null;
    
    if (lastSnapshot) {
      // Compare node count and edge count as a quick check
      const sameNodeCount = lastSnapshot.nodes.length === currentNodes.length;
      const sameEdgeCount = lastSnapshot.edges.length === currentEdgesClone.length;
      
      if (sameNodeCount && sameEdgeCount) {
        // If counts match, do a deeper check on node positions, data, and edge connections
        const nodesEssentiallyUnchanged = currentNodes.every(node => {
          const lastNode = lastSnapshot.nodes.find(n => n.id === node.id);
          if (!lastNode) return false; // Node added or removed, so state has changed

          // Compare positions with a threshold
          const positionEssentiallyUnchanged =
            Math.abs(lastNode.position.x - node.position.x) < 0.001 &&
            Math.abs(lastNode.position.y - node.position.y) < 0.001;

          // Compare data (simple stringify for now, could be more granular)
          // This helps catch changes in node labels, colors, etc.
          const dataEssentiallyUnchanged = JSON.stringify(lastNode.data) === JSON.stringify(node.data);
          
          // Compare dimensions (width/height)
          const dimensionsEssentiallyUnchanged = 
            (lastNode.width === node.width || (lastNode.width === undefined && node.width === null) || (lastNode.width === null && node.width === undefined)) &&
            (lastNode.height === node.height || (lastNode.height === undefined && node.height === null) || (lastNode.height === null && node.height === undefined));

          return positionEssentiallyUnchanged && dataEssentiallyUnchanged && dimensionsEssentiallyUnchanged;
        });
        
        const edgesEssentiallyUnchanged = currentEdgesClone.every(edge => {
          const lastEdge = lastSnapshot.edges.find(e => e.id === edge.id);
          if (!lastEdge) return false; // Edge added or removed

          // Check essential connection properties and data
          const dataEssentiallyUnchanged = JSON.stringify(lastEdge.data) === JSON.stringify(edge.data);
          const styleEssentiallyUnchanged = JSON.stringify(lastEdge.style) === JSON.stringify(edge.style);


          return edge.source === lastEdge.source &&
                 edge.target === lastEdge.target &&
                 edge.sourceHandle === lastEdge.sourceHandle &&
                 edge.targetHandle === lastEdge.targetHandle &&
                 dataEssentiallyUnchanged &&
                 styleEssentiallyUnchanged;
        });
        
        // If nothing meaningful (visibly or data-wise) changed, don't take a new snapshot
        if (nodesEssentiallyUnchanged && edgesEssentiallyUnchanged) {
          return;
        }
      }
    }
    
    if (past.length === 0 && currentNodes.length === 0 && currentEdgesClone.length === 0 && !get().isBatchUpdating) {
        return;
    }

    // Add the new snapshot to the past array, keeping only the last 20
    set((state) => ({
      past: [...state.past.slice(-19), { nodes: currentNodes, edges: currentEdgesClone }],
      future: [], // Clear future stack when a new snapshot is taken
    }));
    
  },

  undo: () => {
    const { past, nodes: currentNodes, edges: currentEdges, future: currentFuture } = get(); // Get current state

    if (past.length === 0) {
      return false; // Indicate failure: nothing to undo
    }

    // Set the flag to prevent snapshots during undo operation
    undoRedoState.setIsRestoring(true);

    // Get the previous state (the one we want to restore)
    const previousState = past[past.length - 1];
    const newPast = past.slice(0, past.length - 1);
    
    // Log node positions for debugging
    if (currentNodes.length > 0 && previousState.nodes.length > 0) {
    }

    // Create a snapshot of the current state for the future stack
    const currentStateForFuture = {
      nodes: currentNodes.map(node => ({
        ...node,
        selected: false,
        dragging: false,
      })),
      edges: currentEdges,
    };

    // Check if we're undoing a node deletion by comparing node counts
    const isUndoingNodeDeletion = previousState.nodes.length > currentNodes.length;
    
    // If we're undoing a node deletion, use all edges from the previous state
    // Otherwise, validate edges to ensure they're still valid
    const edgesToRestore = isUndoingNodeDeletion
      ? previousState.edges // Use all edges from previous state when undoing deletion
      : validateEdgesWithHandles(previousState.nodes, previousState.edges);

    // Update the state with the previous snapshot
    set({
      past: newPast,
      future: [currentStateForFuture, ...currentFuture], // Add current state to future
      nodes: previousState.nodes,
      edges: edgesToRestore, // Use all edges or validated edges based on operation type
    });

    // Clear the flag *after* the current execution context finishes
    setTimeout(() => undoRedoState.setIsRestoring(false), 0);

    // After state is restored, we need to give React Flow time to render the nodes
    // before we can update node internals. Using setTimeout to ensure this happens
    // after the current execution context.
    // Clear the flag *after* the current execution context finishes
    setTimeout(() => undoRedoState.setIsRestoring(false), 0);

    get().recalculateClusterLatencies();
    return true; // Indicate success
  },

  redo: () => {
    const { future, nodes: currentNodes, edges: currentEdges, past } = get(); // Get current state

    if (future.length === 0) {
      return false; // Indicate failure: nothing to redo
    }

    // Set the flag to prevent snapshots during redo operation
    undoRedoState.setIsRestoring(true);

    // Get the next state (the one we want to restore)
    const nextState = future[0];
    const newFuture = future.slice(1);
    
    // Log node positions for debugging
    if (currentNodes.length > 0 && nextState.nodes.length > 0) {
    }

    // Create a snapshot of the current state for the past stack
    const currentStateForPast = {
      nodes: currentNodes.map(node => ({
        ...node,
        selected: false,
        dragging: false,
      })),
      edges: currentEdges,
    };

    // Check if we're redoing a node addition by comparing node counts
    const isRedoingNodeAddition = nextState.nodes.length > currentNodes.length;
    
    // If we're redoing a node addition, use all edges from the next state
    // Otherwise, validate edges to ensure they're still valid
    const edgesToRestore = isRedoingNodeAddition
      ? nextState.edges // Use all edges from next state when redoing addition
      : validateEdgesWithHandles(nextState.nodes, nextState.edges);

    // Update the state with the next snapshot
    set({
      past: [...past, currentStateForPast], // Add current state to past
      future: newFuture,
      nodes: nextState.nodes,
      edges: edgesToRestore, // Use all edges or validated edges based on operation type
    });

    // Log state after update for debugging
    setTimeout(() => {
      const afterState = get();
      if (afterState.nodes.length > 0) {
      }
    }, 0);

    // Clear the flag *after* the current execution context finishes
    setTimeout(() => undoRedoState.setIsRestoring(false), 0);

    // React Flow should update itself when `nodes` and `edges` props change.
    // Explicitly calling updateNodeInternals or setViewport here has been unreliable.
    get().recalculateClusterLatencies();
    return true; // Indicate success
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

}));

// Process the initial nodes loaded from persistence
if (initialNodesToProcess.length > 0) {
  setTimeout(() => {
    const processedInitialNodes = processNodes(initialNodesToProcess, () => useStore.getState());
    useStore.setState({ nodes: processedInitialNodes, isInitialized: true });
    // Recalculate latencies after initial nodes are set
    useStore.getState().recalculateClusterLatencies();
    // Take initial snapshot after loading but DON'T clear it
    // This ensures we have a starting point to undo to
    useStore.getState().takeSnapshot();
  }, 0);
} else {
  // If no persisted nodes, mark as initialized immediately
  useStore.setState({ isInitialized: true });
  // Take initial snapshot for empty canvas but DON'T clear it
  // This ensures we have a starting point to undo to
  useStore.getState().takeSnapshot();
}


// Subscribe to state changes for persistence AFTER initial state is set
useStore.subscribe(subscribeToChanges);

export default useStore;
